import type { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../auth/UserContext';
import { useI18n } from '../i18n/I18n';
import type { SavePreferenceFormData } from '../types/savePreference';
import { failedToastMessage } from '../utils/commonUtils';
import type { ColumnViewSelection, FavouriteColumnPresetModel, SavedColumnView } from './columnViewTypes';
import type { GridSettingsPreference } from './DataGrid/gridSettings/types';
import {
    areOrderedFieldsEqual,
    areVisibilityModelsEqual,
    nextGridSettingsDirtyTracking,
    orderedFieldsWithoutGroupingColumn,
    type GridSettingsDirtyTracking
} from './columnViewUtils';
import { ColumnVisibilitySelector } from './ColumnVisibilitySelector';
import { FAVOURITE_COLUMN_PRESET_KEY, MY_COLUMNS, PRESET_INDEX } from './columnVisibilitySelectorConstants';
import {
    buildAllColumnsFavouriteModel,
    findOwnFavouritePreference,
    getFavouritePreferenceModel,
    isStaleSavedColumnViewFavourite,
    stripFavouriteFields,
    type ColumnVisibilityPreset
} from './ColumnVisibiltyUtills';

type UseColumnVisibilityOptions = {
    enableSaveColumnView?: boolean;
    /** Column visibility edits mark the view dirty. */
    trackColumnVisibility?: boolean;
    /** Column order is restored and marks the view dirty. */
    trackColumnOrder?: boolean;
    savedColumnViews?: SavedColumnView[];
    onSaveColumnView?: (formData: SavePreferenceFormData) => Promise<SavedColumnView | null>;
    onUpdateColumnView?: () => Promise<SavedColumnView | null>;
    onDeleteColumnView?: (viewId: string) => Promise<boolean>;
    savingColumnView?: boolean;
    deletingColumnView?: boolean;
    getCurrentOrderedFields?: () => string[];
    defaultOrderedFields?: string[];
    /** Bumped when the user reorders columns so dirty state re-evaluates. */
    columnOrderRevision?: number;
    /** True after the user drag-reorders columns (cleared on apply/save). */
    columnOrderDirty?: boolean;
    onOrderedFieldsApplied?: (orderedFields: string[] | null | undefined) => void;
    getCurrentGridSettings?: () => GridSettingsPreference | undefined;
    defaultGridSettings?: GridSettingsPreference;
    /** Bumped when sort/group changes so dirty state re-evaluates. */
    gridSettingsRevision?: number;
    /** `null` restores the grid default sort and group. */
    onGridSettingsApplied?: (settings: GridSettingsPreference | null) => void;
};

export function useColumnVisibility(
    id: string,
    presetsLabelToken: string,
    defaultColumnVisibility: GridColumnVisibilityModel,
    columnVisibilityPresets: ColumnVisibilityPreset[],
    defaultSelectedPreset: number = PRESET_INDEX.all,
    enabledFavouritePreset: boolean = false,
    hideMyColumnsPreset: boolean = false,
    defaultSelectedPresetLabel?: string,
    hideAllColumnsPreset?: boolean,
    hideDefaultColumnsPreset?: boolean,
    options: UseColumnVisibilityOptions = {}
) {
    const {
        enableSaveColumnView = false,
        trackColumnVisibility = enableSaveColumnView,
        trackColumnOrder = false,
        savedColumnViews = [],
        onSaveColumnView,
        onUpdateColumnView,
        onDeleteColumnView,
        savingColumnView = false,
        deletingColumnView = false,
        getCurrentOrderedFields,
        defaultOrderedFields,
        columnOrderRevision = 0,
        columnOrderDirty = false,
        onOrderedFieldsApplied,
        getCurrentGridSettings,
        defaultGridSettings,
        gridSettingsRevision = 0,
        onGridSettingsApplied
    } = options;

    const { getPreference, preferences, savePreference, preferencesLoaded, userProfile, application } =
        useContext(UserContext);

    const customColumnVisibility = useMemo(() => {
        if (preferencesLoaded) {
            return getPreference(id, MY_COLUMNS);
        }
    }, [getPreference, id, preferencesLoaded]);

    const [selectedPreset, setSelectedPreset] = useState<number>(defaultSelectedPreset);
    const [selectedSavedViewId, setSelectedSavedViewId] = useState<string | null>(null);

    const [columnVisibiltyModel, setColumnVisibiltyModel] = useState<FavouriteColumnPresetModel>(() => ({
        ...defaultColumnVisibility
    }));
    /** Visibility snapshot after the last intentional apply/hydrate — used for Save View dirty when no saved view is selected. */
    const [baselineVisibilityModel, setBaselineVisibilityModel] = useState<Record<string, boolean>>(() =>
        stripFavouriteFields({ ...defaultColumnVisibility })
    );
    const t = useI18n();

    function captureBaselineVisibility(model: FavouriteColumnPresetModel) {
        setBaselineVisibilityModel(stripFavouriteFields(model));
    }

    const applyingSelectionRef = useRef(false);
    useEffect(function clearApplyingSelection() {
        if (!applyingSelectionRef.current) {
            return;
        }
        const frame = requestAnimationFrame(function finishApplyingSelection() {
            applyingSelectionRef.current = false;
        });
        return function cancelFinishApplyingSelection() {
            cancelAnimationFrame(frame);
        };
    });

    const setColumnVisibilityFromGrid = useCallback(function setColumnVisibilityFromGrid(
        model: FavouriteColumnPresetModel
    ) {
        // View switches set the model, then MUI echoes the previous view's columns.
        // Applying that echo puts Cost Details columns back while the label says All columns.
        if (applyingSelectionRef.current) {
            return;
        }
        setColumnVisibiltyModel(model);
    }, []);

    const applySelection = (index: number, selection: ColumnViewSelection) => {
        applyingSelectionRef.current = true;
        const wasSavedView = selectedSavedViewId != null;
        const isSavedView = Boolean(selection.savedViewId);
        // Snapshot before visibility changes. MUI often appends newly-shown columns at the end;
        // re-applying this snapshot after the view/favourite switch keeps order stable.
        const orderSnapshot =
            trackColumnOrder && typeof getCurrentOrderedFields === 'function' ? getCurrentOrderedFields() : [];

        if (typeof onGridSettingsApplied === 'function') {
            const nextGridSettings = selection.savedViewId ? (selection.gridSettings ?? null) : null;
            onGridSettingsApplied(nextGridSettings);
        }

        setSelectedPreset(index);
        setSelectedSavedViewId(selection.savedViewId ?? null);
        setColumnVisibiltyModel(selection.visibilityModel);
        if (trackColumnVisibility) {
            captureBaselineVisibility(selection.visibilityModel);
        }

        if (trackColumnOrder) {
            if (selection.orderedFields?.length) {
                onOrderedFieldsApplied?.(selection.orderedFields);
            } else if (wasSavedView && !isSavedView) {
                onOrderedFieldsApplied?.(null);
            } else if (orderSnapshot.length) {
                onOrderedFieldsApplied?.(orderSnapshot);
            }
        }
    };

    // Layout effect so the favourite replaces the default preset before the first paint.
    useLayoutEffect(() => {
        if (!preferencesLoaded) return;

        // Same guard as a view click. Restoring a favourite sets the saved columns, then MUI
        // echoes the previous All Columns model. Applying that echo shows every column and Save View.
        applyingSelectionRef.current = true;
        let matched = false;
        if (preferences?.length > 0 && enabledFavouritePreset) {
            const favourite = findOwnFavouritePreference(preferences, id, userProfile?.accountId, application?.name);
            const parsed = getFavouritePreferenceModel(favourite);
            const staleSavedView = enableSaveColumnView && isStaleSavedColumnViewFavourite(parsed, savedColumnViews);

            if (favourite && parsed && staleSavedView) {
                setSelectedPreset(PRESET_INDEX.all);
                setSelectedSavedViewId(null);
                setColumnVisibiltyModel({});
                if (enableSaveColumnView) {
                    captureBaselineVisibility({});
                }
                void (async function clearStaleFavouritePreference() {
                    try {
                        await savePreference(
                            id,
                            FAVOURITE_COLUMN_PRESET_KEY,
                            JSON.stringify(buildAllColumnsFavouriteModel())
                        );
                    } catch {
                        failedToastMessage('grid.preference.failedToSave.text');
                    }
                })();
                matched = true;
            } else if (favourite && parsed) {
                const currentIndex = parsed.currentIndex;
                setSelectedPreset(currentIndex);
                setSelectedSavedViewId(parsed.savedViewId ?? null);
                setColumnVisibiltyModel(parsed);
                if (enableSaveColumnView) {
                    captureBaselineVisibility(parsed);
                }
                // Only restore order for favourited saved views when column views are enabled.
                if (trackColumnOrder && parsed.savedViewId && parsed.orderedFields?.length) {
                    onOrderedFieldsApplied?.(parsed.orderedFields);
                }
                if (parsed.savedViewId && typeof onGridSettingsApplied === 'function') {
                    const savedView = savedColumnViews.find((view) => view.id === parsed.savedViewId);
                    onGridSettingsApplied(savedView?.gridSettings ?? null);
                }
                matched = true;

                const presetModel: GridColumnVisibilityModel = {};
                let resolvedPresetModel = false;
                if (currentIndex === PRESET_INDEX.all) {
                    resolvedPresetModel = true;
                } else if (currentIndex === PRESET_INDEX.default) {
                    Object.assign(presetModel, defaultColumnVisibility);
                    resolvedPresetModel = true;
                } else if (currentIndex === PRESET_INDEX.my) {
                    Object.assign(presetModel, customColumnVisibility || {});
                    resolvedPresetModel = true;
                } else if (currentIndex === PRESET_INDEX.saved && parsed.savedViewId) {
                    const savedView = savedColumnViews.find((view) => view.id === parsed.savedViewId);
                    if (savedView) {
                        Object.assign(presetModel, savedView.columnVisibilityModel);
                        resolvedPresetModel = true;
                    }
                } else if (currentIndex !== PRESET_INDEX.saved && columnVisibilityPresets?.[currentIndex]) {
                    columnVisibilityPresets[currentIndex].hidden?.forEach((colName) => {
                        presetModel[colName] = false;
                    });
                    resolvedPresetModel = true;
                }

                const samePresetColumnConfig =
                    JSON.stringify(presetModel) === JSON.stringify(stripFavouriteFields(parsed));
                if (!samePresetColumnConfig && resolvedPresetModel) {
                    const reconciledModel: FavouriteColumnPresetModel = {
                        presetKey: parsed.presetKey,
                        currentFavourite: parsed.currentFavourite,
                        currentIndex: parsed.currentIndex,
                        ...(parsed.orderedFields ? { orderedFields: parsed.orderedFields } : {}),
                        ...(parsed.savedViewId ? { savedViewId: parsed.savedViewId } : {}),
                        ...presetModel
                    };
                    setColumnVisibiltyModel(reconciledModel);
                    if (enableSaveColumnView) {
                        captureBaselineVisibility(reconciledModel);
                    }
                }
            }
        }

        if (!matched) {
            if (defaultSelectedPresetLabel) {
                const matchedPreset = columnVisibilityPresets.find(
                    (preset) => preset.label === defaultSelectedPresetLabel
                );
                if (matchedPreset) {
                    const model: GridColumnVisibilityModel = {};
                    matchedPreset.hidden.forEach((colName) => {
                        model[colName] = false;
                    });
                    const index = columnVisibilityPresets.findIndex((p) => p.label === defaultSelectedPresetLabel);
                    setSelectedPreset(index);
                    setSelectedSavedViewId(null);
                    setColumnVisibiltyModel(model);
                    if (enableSaveColumnView) {
                        captureBaselineVisibility(model);
                    }
                    return;
                }
            }

            if (defaultSelectedPreset > -1) {
                const newModel = {};
                columnVisibilityPresets[defaultSelectedPreset]?.hidden?.forEach((colName) => {
                    newModel[colName] = false;
                });
                setSelectedPreset(defaultSelectedPreset);
                setSelectedSavedViewId(null);
                setColumnVisibiltyModel(newModel);
                if (enableSaveColumnView) {
                    captureBaselineVisibility(newModel);
                }
            } else {
                setSelectedPreset(defaultSelectedPreset);
                setSelectedSavedViewId(null);
                const fallbackModel = cloneDeep(
                    isEmpty(customColumnVisibility) ? defaultColumnVisibility : customColumnVisibility
                );
                setColumnVisibiltyModel(fallbackModel);
                if (enableSaveColumnView) {
                    captureBaselineVisibility(fallbackModel);
                }
            }
        }
        // Hydrate favourite once preferences are ready (single owner — selector only updates star UI).
        // Intentionally omit savedColumnViews from deps; selector apply refreshes views separately.
    }, [preferencesLoaded, defaultSelectedPreset, userProfile?.accountId, application?.name]);

    const gridSettingsDirtyTrackingRef = useRef<GridSettingsDirtyTracking>({
        preset: selectedPreset,
        savedViewId: selectedSavedViewId,
        revision: gridSettingsRevision,
        dirty: false
    });

    const settingsDifferFromSelection = function settingsDifferFromSelection() {
        if (typeof getCurrentGridSettings !== 'function') {
            return false;
        }

        const current = getCurrentGridSettings() ?? {};
        if (selectedSavedViewId) {
            const savedView = savedColumnViews.find((view) => view.id === selectedSavedViewId);
            return !isEqual(current, savedView?.gridSettings ?? defaultGridSettings ?? {});
        }

        return !isEqual(current, defaultGridSettings ?? {});
    };

    gridSettingsDirtyTrackingRef.current = nextGridSettingsDirtyTracking(gridSettingsDirtyTrackingRef.current, {
        preset: selectedPreset,
        savedViewId: selectedSavedViewId,
        revision: gridSettingsRevision,
        settingsDiffer: settingsDifferFromSelection()
    });
    const gridSettingsDirty = gridSettingsDirtyTrackingRef.current.dirty;

    const hasUnsavedColumnViewChanges = useMemo(() => {
        if (!enableSaveColumnView) {
            return false;
        }

        const currentVisibility = stripFavouriteFields(columnVisibiltyModel);
        const currentDataOrder =
            trackColumnOrder && typeof getCurrentOrderedFields === 'function'
                ? orderedFieldsWithoutGroupingColumn(getCurrentOrderedFields())
                : [];

        if (trackColumnOrder && columnOrderDirty) {
            const expectedOrder = selectedSavedViewId
                ? orderedFieldsWithoutGroupingColumn(
                      savedColumnViews.find((view) => view.id === selectedSavedViewId)?.orderedFields
                  )
                : orderedFieldsWithoutGroupingColumn(defaultOrderedFields);
            if (!areOrderedFieldsEqual(currentDataOrder, expectedOrder)) {
                return true;
            }
        }

        if (selectedSavedViewId) {
            const savedView = savedColumnViews.find((view) => view.id === selectedSavedViewId);
            if (!savedView) {
                return false;
            }

            const orderChanged =
                trackColumnOrder &&
                typeof getCurrentOrderedFields === 'function' &&
                !areOrderedFieldsEqual(currentDataOrder, orderedFieldsWithoutGroupingColumn(savedView.orderedFields));
            const visibilityChanged =
                trackColumnVisibility && !areVisibilityModelsEqual(currentVisibility, savedView.columnVisibilityModel);

            return orderChanged || gridSettingsDirty || visibilityChanged;
        }

        if (gridSettingsDirty) {
            return true;
        }

        if (!trackColumnVisibility) {
            return false;
        }

        // Built-in preset / no saved view: a visibility edit vs the last apply can be saved as a new view.
        return !areVisibilityModelsEqual(currentVisibility, baselineVisibilityModel);
    }, [
        enableSaveColumnView,
        trackColumnOrder,
        trackColumnVisibility,
        columnOrderDirty,
        getCurrentOrderedFields,
        defaultOrderedFields,
        columnVisibiltyModel,
        selectedSavedViewId,
        savedColumnViews,
        baselineVisibilityModel,
        columnOrderRevision,
        gridSettingsDirty
    ]);

    let selectedPresetLabel;
    switch (selectedPreset) {
        case PRESET_INDEX.all:
            selectedPresetLabel = t('grid.column.presets.all');
            break;
        case PRESET_INDEX.default:
            selectedPresetLabel = t('grid.column.presets.default');
            break;
        case PRESET_INDEX.my:
            selectedPresetLabel = t('grid.column.presets.my');
            break;
        case PRESET_INDEX.saved:
            selectedPresetLabel = savedColumnViews.find((view) => view.id === selectedSavedViewId)?.name ?? null;
            break;
        default:
            selectedPresetLabel = columnVisibilityPresets?.[selectedPreset]?.label ?? null;
    }

    const component = (
        <ColumnVisibilitySelector
            id={id}
            labelToken={presetsLabelToken}
            key={'presets'}
            defaultColumnVisibility={defaultColumnVisibility}
            customColumnVisibility={customColumnVisibility}
            presets={columnVisibilityPresets}
            selectedPreset={selectedPreset}
            selectedSavedViewId={selectedSavedViewId}
            onPresetSelected={applySelection}
            enabledFavouritePreset={enabledFavouritePreset}
            hideMyColumnsPreset={hideMyColumnsPreset}
            hideAllColumnsPreset={hideAllColumnsPreset}
            hideDefaultColumnsPreset={hideDefaultColumnsPreset}
            enableSaveColumnView={enableSaveColumnView}
            savedColumnViews={savedColumnViews}
            onSaveColumnView={onSaveColumnView}
            onUpdateColumnView={onUpdateColumnView}
            onDeleteColumnView={onDeleteColumnView}
            savingColumnView={savingColumnView}
            deletingColumnView={deletingColumnView}
            hasUnsavedColumnViewChanges={hasUnsavedColumnViewChanges}
        />
    );

    return {
        selectPreset: (index: number, model: GridColumnVisibilityModel) => {
            setSelectedPreset(index);
            setSelectedSavedViewId(null);
            setColumnVisibiltyModel(model);
            savePreference(id, MY_COLUMNS, JSON.stringify(model));
        },
        setColumnVisibiltyModel: setColumnVisibilityFromGrid,
        selectedPreset,
        selectedPresetLabel,
        selectedSavedViewId,
        columnVisibiltyModel,
        ColumnVisibilitySelector: component
    };
}
